import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popper, TextField } from '@mui/material';
import classnames from 'classnames';
// Reuse the legacy detected-paths chips (same suggestions from the response sample).
import DataPathSuggestions from 'js/components/data_sources/add_ds_form/DataPathSuggestions';
import { recordCountAtPath } from './jsonPath';
import classes from './ResponsePanel.module.scss';

/**
 * Faithful in-card Response tabs (prototype StepCard body):
 * Response Data / Response Metadata / Request Headers.
 *
 * USABILITY-ONLY: reads the existing per-step config keys and writes via
 * `onChange(key, value)` → `onAPIConfigChanged(key, value, stageIndex)`:
 *   - response.data.path             (Data Path JSONPath + Detected paths chips)
 *   - response.data.path.additional  (Metadata path)
 *   - request.headers                (KEY/VALUE grid; serialized to the same
 *                                     comma-separated `key:value` string)
 * NO response.format control (content-type/backend driven).
 */

export interface ResponsePanelProps {
  stageIndex: number;
  stageConfig: Record<string, any>;
  /** active step's probe response, for the detected-paths chips */
  apiResponse?: any;
  /** caller forwards to onAPIConfigChanged(key, value, stageIndex) */
  onChange: (key: string, value: any) => void;
  /** show the Request Body tab (method has a body: POST/PUT/PATCH) */
  showBody?: boolean;
  /** body editor node, rendered in the Request Body tab */
  bodyContent?: React.ReactNode;
  /** focusing the data/metadata path field switches the global preview tab */
  onFieldFocus?: (tab: 'data' | 'metadata') => void;
}

type ResponseTab = 'body' | 'data' | 'metadata' | 'headers';

interface HeaderRow {
  key: string;
  value: string;
}

/** Common HTTP request headers offered as autocomplete suggestions for KEY. */
const COMMON_REQUEST_HEADERS = [
  'Accept',
  'Accept-Charset',
  'Accept-Encoding',
  'Accept-Language',
  'Authorization',
  'Cache-Control',
  'Content-Type',
  'Cookie',
  'Host',
  'If-Match',
  'If-Modified-Since',
  'If-None-Match',
  'Origin',
  'Range',
  'Referer',
  'User-Agent',
  'X-Api-Key',
  'X-Requested-With',
];

/**
 * Common VALUES suggested for well-known headers (keyed by lowercased header name).
 * Free-form typing still allowed — these only seed the autosuggest.
 * NOTE: values must not contain commas; `request.headers` is serialized as a
 * comma-separated `key:value` string, so a comma would split the pair on re-parse.
 */
const COMMON_HEADER_VALUES: Record<string, string[]> = {
  accept: ['application/json', 'application/xml', 'text/csv', 'text/plain', '*/*'],
  'content-type': [
    'application/json',
    'application/xml',
    'application/x-www-form-urlencoded',
    'text/plain',
    'text/csv',
  ],
  'accept-encoding': ['gzip', 'deflate', 'br', 'identity'],
  'accept-charset': ['utf-8', 'iso-8859-1'],
  'accept-language': ['en-US', 'en'],
  'cache-control': ['no-cache', 'no-store', 'max-age=0'],
  'x-requested-with': ['XMLHttpRequest'],
};

/** Shared field style so all redesign form inputs use the same mono 12px font. */
const FIELD_SX = {
  '& .MuiInputBase-input': {
    fontFamily: 'var(--font-family-mono, monospace)',
    fontSize: '12px',
  },
};

/** Legacy labels response.data.path "(JSONPath)" for JSON, "(XPath)" for XML. */
const dataPathSuffix = (contentType?: string): string =>
  contentType && /xml/i.test(contentType) ? '(XPath)' : '(JSONPath)';

/** Parse the legacy comma-separated `key:value` header string into rows. */
const parseHeaders = (raw: string): HeaderRow[] => {
  if (!raw || !raw.trim()) return [];
  return raw
    .split(',')
    .map((pair) => {
      const idx = pair.indexOf(':');
      if (idx === -1) return { key: pair.trim(), value: '' };
      return { key: pair.slice(0, idx).trim(), value: pair.slice(idx + 1).trim() };
    })
    .filter((r) => r.key !== '' || r.value !== '');
};

/** Serialize rows back to the legacy `key:value` comma-separated string. */
const serializeHeaders = (rows: HeaderRow[]): string =>
  rows
    .filter((r) => r.key.trim() !== '')
    .map((r) => `${r.key.trim()}:${r.value}`)
    .join(', ');

export const ResponsePanel: React.FC<ResponsePanelProps> = ({
  stageIndex,
  stageConfig,
  apiResponse,
  onChange,
  showBody = false,
  bodyContent,
  onFieldFocus,
}) => {
  const [tab, setTab] = useState<ResponseTab>(showBody ? 'body' : 'data');
  // Row index + column + anchor of the header cell showing the autosuggest (-1 = none).
  const [suggestRow, setSuggestRow] = useState<number>(-1);
  const [suggestCol, setSuggestCol] = useState<'key' | 'value'>('key');
  const [suggestAnchor, setSuggestAnchor] = useState<HTMLElement | null>(null);
  // Pending deferred-close from a blur. Opening a suggest for another cell must
  // cancel it, or the stale timer instantly closes the newly opened dropdown.
  const suggestCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingSuggestClose = () => {
    if (suggestCloseTimer.current) {
      clearTimeout(suggestCloseTimer.current);
      suggestCloseTimer.current = null;
    }
  };

  const closeSuggest = () => {
    cancelPendingSuggestClose();
    setSuggestRow(-1);
    setSuggestAnchor(null);
  };

  // Deferred close on blur, so a suggestion mousedown can land first.
  const scheduleSuggestClose = () => {
    cancelPendingSuggestClose();
    suggestCloseTimer.current = setTimeout(closeSuggest, 150);
  };

  const openSuggest = (index: number, col: 'key' | 'value', anchor: HTMLElement) => {
    cancelPendingSuggestClose();
    setSuggestRow(index);
    setSuggestCol(col);
    setSuggestAnchor(anchor);
  };

  useEffect(() => cancelPendingSuggestClose, []);

  // Open the Request Body tab by default when the method gains a body (e.g. POST);
  // fall back to Response Data when it loses one.
  useEffect(() => {
    setTab(showBody ? 'body' : 'data');
  }, [showBody]);

  const dataPath: string = stageConfig['response.data.path'] ?? '';
  const metadataPath: string = stageConfig['response.data.path.additional'] ?? '';
  const headersRaw: string = (stageConfig['request.headers'] ?? '').trim();
  const headerRows = parseHeaders(stageConfig['request.headers'] ?? '');
  const suffix = dataPathSuffix(stageConfig.contentType);
  // Count records at the SELECTED data path (matches the Detected-paths chips),
  // falling back to the probe's reported length.
  const recordCount: number | undefined =
    recordCountAtPath(apiResponse?.apiResponseContent, dataPath) ??
    apiResponse?.apiResponseContentLength;
  const bodyBytes: number = (stageConfig['body.template'] ?? '').length;

  // The grid always shows a trailing empty row for adding a new header.
  const editableRows: HeaderRow[] = [...headerRows, { key: '', value: '' }];

  const commitHeaders = (rows: HeaderRow[]) => onChange('request.headers', serializeHeaders(rows));

  const updateHeader = (index: number, patch: Partial<HeaderRow>) => {
    const next = editableRows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    commitHeaders(next);
  };

  const removeHeader = (index: number) => {
    const next = editableRows.filter((_, i) => i !== index);
    commitHeaders(next);
  };

  // Stable reference so the DataPathSuggestions chips don't see a new prop each render.
  const handleDataPathSuggestion = useCallback(
    (path: string) => onChange('response.data.path', path),
    [onChange],
  );

  return (
    <div className={classes.root} data-testid={`response-panel-${stageIndex}`}>
      <div className={classes.tabBar} role="tablist">
        {showBody && (
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'body'}
            className={classnames(classes.tab, { [classes.tabActive]: tab === 'body' })}
            onClick={() => setTab('body')}
            data-testid="response-tab-body"
          >
            <span className={classes.tabTitle}>Request Body</span>
            <span className={classes.tabSubAccent}>{bodyBytes} bytes</span>
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'headers'}
          className={classnames(classes.tab, { [classes.tabActive]: tab === 'headers' })}
          onClick={() => setTab('headers')}
          data-testid="response-tab-headers"
        >
          <span className={classes.tabTitle}>Request Headers</span>
          {headersRaw && <span className={classes.tabSub}>{headersRaw}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'data'}
          className={classnames(classes.tab, { [classes.tabActive]: tab === 'data' })}
          onClick={() => setTab('data')}
          data-testid="response-tab-data"
        >
          <span className={classes.tabTitle}>Response: Select Data</span>
          {recordCount != null && (
            <span className={classes.tabSubAccent}>
              {recordCount} record{recordCount === 1 ? '' : 's'}
            </span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'metadata'}
          className={classnames(classes.tab, { [classes.tabActive]: tab === 'metadata' })}
          onClick={() => setTab('metadata')}
          data-testid="response-tab-metadata"
        >
          <span className={classes.tabTitle}>Response: Select Metadata</span>
          {metadataPath && <span className={classes.tabSub}>{metadataPath}</span>}
        </button>
      </div>

      {tab === 'body' && showBody && (
        <div className={classes.tabPanel} data-testid="response-body-panel">
          {bodyContent}
        </div>
      )}

      {tab === 'data' && (
        <div className={classes.tabPanel}>
          <div className={classes.fieldRow}>
            <label className={classes.fieldLabelInline}>{`Data Path ${suffix}`}</label>
            <TextField
              size="small"
              fullWidth
              value={dataPath}
              placeholder="$.results[*]"
              onChange={(e) => onChange('response.data.path', e.target.value)}
              onFocus={() => onFieldFocus?.('data')}
              sx={FIELD_SX}
              inputProps={{
                'aria-label': 'Data Path',
                'data-testid': 'response-field-data-path',
              }}
            />
          </div>
          {apiResponse?.apiResponseContent && (
            <div className={classes.detectedPaths} data-testid="response-detected-paths">
              <span className={classes.detectedLabel}>Detected paths</span>
              <DataPathSuggestions
                content={apiResponse.apiResponseContent}
                contentType={stageConfig.contentType}
                selectedPath={dataPath}
                recommend
                onSuggestionClicked={handleDataPathSuggestion}
              />
            </div>
          )}
        </div>
      )}

      {tab === 'metadata' && (
        <div className={classes.tabPanel}>
          <div className={classes.fieldRow}>
            <label className={classes.fieldLabelInline}>Path to metadata</label>
            <TextField
              size="small"
              fullWidth
              value={metadataPath}
              placeholder="$.paging"
              onChange={(e) => onChange('response.data.path.additional', e.target.value)}
              onFocus={() => onFieldFocus?.('metadata')}
              sx={FIELD_SX}
              inputProps={{
                'aria-label': 'Path to metadata in response',
                'data-testid': 'response-field-metadata-path',
              }}
            />
          </div>
        </div>
      )}

      {tab === 'headers' && (
        <div className={classes.tabPanel}>
          <label className={classes.fieldLabel}>Request Headers</label>
          <div className={classes.headerGrid} data-testid="response-headers-grid">
            <div className={classes.headerGridHead}>
              <span>Key</span>
              <span>Value</span>
              <span />
            </div>
            {editableRows.map((row, index) => {
              const isTrailing = index === editableRows.length - 1;
              return (
                <div className={classes.headerGridRow} key={index}>
                  <div className={classes.headerKeyCell}>
                    <input
                      className={classes.headerCell}
                      value={row.key}
                      placeholder="Header name"
                      autoComplete="off"
                      onChange={(e) => {
                        updateHeader(index, { key: e.target.value });
                        openSuggest(index, 'key', e.currentTarget);
                      }}
                      onFocus={(e) => openSuggest(index, 'key', e.currentTarget)}
                      onBlur={scheduleSuggestClose}
                      data-testid={`response-header-key-${index}`}
                    />
                  </div>
                  <input
                    className={classes.headerCell}
                    value={row.value}
                    placeholder="Value"
                    autoComplete="off"
                    onChange={(e) => {
                      updateHeader(index, { value: e.target.value });
                      openSuggest(index, 'value', e.currentTarget);
                    }}
                    onFocus={(e) => openSuggest(index, 'value', e.currentTarget)}
                    onBlur={scheduleSuggestClose}
                    data-testid={`response-header-value-${index}`}
                  />
                  <button
                    type="button"
                    className={classes.headerRemove}
                    disabled={isTrailing}
                    aria-label="Remove header"
                    onClick={() => removeHeader(index)}
                    data-testid={`response-header-remove-${index}`}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          {/* Autosuggest below the focused KEY/VALUE input. KEY suggests common
              header names; VALUE suggests common values for the row's header
              (only well-known ones have any). Popper portals out of the card so
              it isn't clipped by the card's overflow. */}
          {(() => {
            const activeRow =
              suggestRow >= 0 && suggestRow < editableRows.length ? editableRows[suggestRow] : null;
            let headerSuggestions: string[] = [];
            if (activeRow) {
              const typed = (suggestCol === 'key' ? activeRow.key : activeRow.value)
                .trim()
                .toLowerCase();
              const pool =
                suggestCol === 'key'
                  ? COMMON_REQUEST_HEADERS
                  : (COMMON_HEADER_VALUES[activeRow.key.trim().toLowerCase()] ?? []);
              // Show the full pool on focus (empty input); filter as the user types.
              headerSuggestions = pool.filter(
                (s) =>
                  s.toLowerCase() !== typed && (typed === '' || s.toLowerCase().includes(typed)),
              );
            }
            return (
              <Popper
                open={!!suggestAnchor && headerSuggestions.length > 0}
                anchorEl={suggestAnchor}
                placement="bottom-start"
                style={{ zIndex: 1400, width: suggestAnchor?.offsetWidth }}
              >
                <ul
                  className={classes.headerSuggest}
                  data-testid={`response-header-suggest-${suggestCol}-${suggestRow}`}
                >
                  {headerSuggestions.map((s) => (
                    <li key={s}>
                      <button
                        type="button"
                        className={classes.headerSuggestItem}
                        // mousedown fires before the input's blur, so the value sticks
                        onMouseDown={(e) => {
                          e.preventDefault();
                          updateHeader(
                            suggestRow,
                            suggestCol === 'key' ? { key: s } : { value: s },
                          );
                          closeSuggest();
                        }}
                      >
                        {s}
                      </button>
                    </li>
                  ))}
                </ul>
              </Popper>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default ResponsePanel;
